import React, { useState, useEffect, useContext, useRef } from "react";
import { Container, Row, Col, Card, CardBody, Input, Button, Modal, ModalHeader, ModalBody, Table } from "reactstrap";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { ThemeContext } from "../contexts/ThemeContext.js";
import { listOrders } from "../redux/actions/orderActions.js";
import { useDebounce } from 'use-debounce';
import axios from "../customAxios.js";

const PurchaseList = () => {
  const { theme } = useContext(ThemeContext);
  const dispatch = useDispatch();
  const isFirstRender = useRef(true);

  const [datas, setDatas] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVouchers, setSelectedVouchers] = useState([]);

  // Get orders and pagination from Redux
  const { orders: ordersObj, loading } = useSelector((state) => state.orderList);

  const orders = ordersObj?.orders || [];
  const pagination = ordersObj?.pagination || {};

  // DataTable columns
  const columns = [
    { name: <b>S.No</b>, cell: (row, index) => index + 1 + ((pagination?.currentPage - 1) * pagination?.itemsPerPage) },
    { name: <b>Order ID</b>, selector: (row) => row?._id, wrap: true, width: "200px" },
    { name: <b>Transaction ID</b>, selector: (row) => row?.txnid, wrap: true, width: "200px" },
    { name: <b>Amount</b>, selector: (row) => `Rs.${row?.amount}` },
    { name: <b>Order Status</b>, selector: (row) => row?.status },
    { name: <b>Order Date</b>, selector: (row) => new Date(row?.createdAt).toLocaleDateString("en-GB") },
    { name: <b>Buyer Name</b>, selector: (row) => row?.buyer?.name },
    { name: <b>Buyer Number</b>, selector: (row) => row?.buyer?.mobilenumber },
    { name: <b>Whatsapp Number</b>, selector: (row) => row?.buyer?.whatsappnumber },
    { name: <b>Buyer Email</b>, selector: (row) => row?.buyer?.email, wrap: true, width: "200px" },
    {
      name: <b>Vouchers</b>,
      cell: (row) => (
        <Button
          color="info"
          size="sm"
          onClick={() => {
            setSelectedVouchers(row?.vouchers || []);
            setModalOpen(true);
          }}
        >
          View
        </Button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  // Fetch orders with pagination and search
  useEffect(() => {
    dispatch(listOrders(currentPage, itemsPerPage, debouncedSearchTerm));
  }, [dispatch, currentPage, itemsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    if (orders) {
      setDatas(orders);
    }
  }, [orders]);

  // Download handler
  const handleDownload = async () => {
    try {
      let url = `/api/v1/admin/download-purchases?`;
      if (fromDate) url += `from=${fromDate}&`;
      if (toDate) url += `to=${toDate}&`;
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `purchases_${fromDate || ''}_${toDate || ''}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert('Download failed: ' + (err.response?.data?.message || err.message));
    }
  };

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col sm="6">
          <h4>Order List</h4>
        </Col>
        <Col sm="6" className="text-right d-flex justify-content-end align-items-center gap-2">
          <Input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ maxWidth: 220 }}
          />
          <Input
            type="date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            placeholder="From Date"
            style={{ maxWidth: 150 }}
          />
          <Input
            type="date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            placeholder="To Date"
            style={{ maxWidth: 150 }}
          />
          <Button color="success" onClick={handleDownload}>
            Download
          </Button>
        </Col>
      </Row>

      <Card>
        <CardBody>
          <DataTable
            pagination
            columns={columns}
            data={datas}
            theme={theme === "dark" ? "darkMode" : "lightMode"}
            paginationServer
            paginationTotalRows={pagination?.totalItems || 0}
            paginationPerPage={pagination?.itemsPerPage || itemsPerPage}
            onChangePage={page => setCurrentPage(page)}
            onChangeRowsPerPage={perPage => setItemsPerPage(perPage)}
            progressPending={loading}
          />
        </CardBody>
      </Card>

      {/* Modal for Voucher Details */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="lg">
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>Voucher Details</ModalHeader>
        <ModalBody>
          <div className="table-responsive">
            <Table bordered>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Voucher ID</th>
                  <th>Brand ID</th>
                  <th>Sold Price</th>
                  <th>Expire Date</th>
                  <th>Actual Price</th>
                  <th>Seller Name</th>
                  <th>Seller Mobile</th>
                  <th>Seller Whatsapp</th>
                  <th>Seller Email</th>
                </tr>
              </thead>
              <tbody>
                {selectedVouchers.map((voucher, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{voucher?.voucherId || "N/A"}</td>
                    <td>{voucher?.brandName || "N/A"}</td>
                    <td>Rs.{voucher?.acceptedPrice || "N/A"}</td>
                    <td>
                      {voucher?.expireDate
                        ? new Date(voucher?.expireDate).toLocaleDateString("en-GB")
                        : "N/A"}
                    </td>
                    <td>Rs.{voucher?.actualPrice || "N/A"}</td>
                    <td>{voucher?.sellerName || "N/A"}</td>
                    <td>{voucher?.sellerMobile || "N/A"}</td>
                    <td>{voucher?.sellerWhatsapp || "N/A"}</td>
                    <td>{voucher?.sellerEmail || "N/A"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default PurchaseList;
